import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Globe, Loader2 } from 'lucide-react';
import axios from 'axios';
import config from '../../../config';

const API_URL = config.API_URL;

/**
 * Créer **ou** modifier un site.
 *
 * Une seule modale pour les deux gestes, parce qu'ils portent exactement les mêmes champs. Les
 * écrans en avaient quatre — création et édition, côté Sites et côté Gestionnaire — et elles
 * avaient divergé : l'édition demandait encore une « Description » que la création ne proposait
 * plus, et ni l'adresse ni le type n'étaient modifiables après coup.
 *
 * `site` fourni ⇒ édition. Absent ⇒ création.
 */
const CreateSiteModal = ({ isOpen, onClose, token, onCreated, site = null }) => {
  const enEdition = Boolean(site);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  /**
   * Ce que le site vend. Vide tant que ce n'est pas déclaré.
   *
   * Rien dans les données ne permet de le déduire : un compte dont les conversions s'appellent
   * « achat » peut compter des devis. Les écrans qui adapteront leurs métriques doivent pouvoir
   * distinguer « non déclaré » de « lead », d'où l'absence de valeur par défaut.
   */
  const [typeSite, setTypeSite] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /*
   * Les champs repartent du site à éditer, ou à vide.
   *
   * `site` fait partie des dépendances : rouvrir la modale sur un autre site sans cela
   * afficherait les valeurs du précédent.
   */
  useEffect(() => {
    if (isOpen) {
      setName(site?.name || '');
      setUrl(site?.url || '');
      setTypeSite(site?.typeSite || '');
      setError(null);
    }
  }, [isOpen, site]);

  const handleCreate = async () => {
    if (!name.trim()) {
      setError('Le nom du site est requis');
      return;
    }

    setLoading(true);
    setError(null);

    // Les trois champs partent toujours, y compris vides : la route distingue `undefined`
    // — ne touche à rien — d'une chaîne vide, qui efface. Les omettre rendrait impossible
    // de retirer une adresse ou un type posé par erreur.
    const corps = { name: name.trim(), url: url.trim(), typeSite: typeSite || null };

    try {
      if (enEdition) {
        await axios.put(`${API_URL}/api/sites/${site.id}`, corps, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/sites`, corps, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      onCreated();
      onClose();
    } catch (err) {
      console.error('[SITE_MODAL] Erreur:', err);
      setError(err.response?.data?.error || err.response?.data?.message
        || `Erreur lors de la ${enEdition ? 'modification' : 'création'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCreate();
    }
  };

  if (!isOpen) return null;

  const modalContentStyle = {
    maxWidth: '400px',
    width: '90vw',
    display: 'flex',
    flexDirection: 'column',
    padding: 0,
    margin: 0
  };

  const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '1rem 1.5rem',
    borderBottom: '1px solid #e5e0d5'
  };

  const titleStyle = {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const closeButtonStyle = {
    padding: '0.5rem',
    borderRadius: '0.375rem',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex'
  };

  const bodyStyle = {
    padding: '1.5rem'
  };

  const labelStyle = {
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    display: 'block',
    marginBottom: '0.5rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    borderRadius: '0.5rem',
    border: '1px solid #e5e0d5',
    fontSize: '0.875rem',
    outline: 'none',
    boxSizing: 'border-box'
  };


  const footerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    padding: '1rem 1.5rem',
    borderTop: '1px solid #e5e0d5',
    backgroundColor: '#f3efe7',
    borderRadius: '0 0 0.75rem 0.75rem',
    margin: 0
  };

  const buttonStyle = (primary) => ({
    padding: '0.625rem 1.25rem',
    borderRadius: '0.5rem',
    border: primary ? 'none' : '1px solid #e5e0d5',
    backgroundColor: primary ? '#1a1a1a' : 'white',
    color: primary ? 'white' : '#64748b',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: !name.trim() && primary ? 'not-allowed' : 'pointer',
    opacity: !name.trim() && primary ? 0.5 : 1,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  });

  return ReactDOM.createPortal(
    <div onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    }}>
      <div style={{
        ...modalContentStyle,
        backgroundColor: 'white',
        borderRadius: '0.75rem',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden'
      }} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={titleStyle}>
            <Globe size={20} />
            {enEdition ? 'Modifier le site' : 'Nouveau site'}
          </div>
          <button style={closeButtonStyle} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div style={bodyStyle}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>
              Nom du site <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="text"
              style={inputStyle}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Mon site"
              autoFocus
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>URL du site</label>
            <input
              type="text"
              style={inputStyle}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="exemple.fr"
            />
            {/* Le protocole est ajouté au besoin par le serveur : l'exiger ici ferait buter
                sur une saisie que tout le monde fait sans. */}
          </div>

          <div>
            <label style={labelStyle}>Type de site</label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[
                { cle: 'ECOMMERCE', libelle: 'E-commerce' },
                { cle: 'LEAD', libelle: 'Lead' }
              ].map(({ cle, libelle }) => (
                <button
                  key={cle}
                  type="button"
                  // Un second clic retire le choix : le type reste facultatif, et sans ce
                  // retour en arrière une sélection faite par erreur serait définitive.
                  onClick={() => setTypeSite(typeSite === cle ? '' : cle)}
                  style={{
                    flex: 1,
                    padding: '0.6rem',
                    borderRadius: '0.5rem',
                    border: `1px solid ${typeSite === cle ? '#1a1a1a' : '#e5e7eb'}`,
                    background: typeSite === cle ? '#1a1a1a' : '#fff',
                    color: typeSite === cle ? '#fff' : '#374151',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'background-color 0.15s, border-color 0.15s'
                  }}
                >
                  {libelle}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fef2f2', borderRadius: '0.5rem', color: '#dc2626', fontSize: '0.875rem' }}>
              {error}
            </div>
          )}
        </div>

        <div style={footerStyle}>
          <button style={buttonStyle(false)} onClick={onClose}>
            Annuler
          </button>
          <button
            style={buttonStyle(true)}
            onClick={handleCreate}
            disabled={!name.trim() || loading}
          >
            {loading ? (
              <>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                {enEdition ? 'Enregistrement...' : 'Création...'}
              </>
            ) : (
              enEdition ? 'Enregistrer' : 'Créer'
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateSiteModal;
